/**
 * features/hr/hrWrite.ts
 * ------------------------------------------------------------------
 * Tiny write helpers used by the HR screens. Centralised so:
 *   - createdAt/updatedAt are always set
 *   - the wrapped setDoc/addDoc/updateDoc from @/lib/firebase are
 *     used (they strip undefined values, matching the rest of the
 *     platform's conventions)
 *   - rule-coupled patterns (audit entries) live in one place
 *
 * In the demo build these are direct client writes (allowed by the
 * hr rules block). In the scoped production build the sensitive
 * ones (payroll, contract changes, leave approval) move behind a
 * Cloud Function callable that does role check + audit log,
 * mirroring approveParentDetailChange.
 * ------------------------------------------------------------------ */
import {
  collection,
  doc,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import { db, setDoc, addDoc, updateDoc } from '@/lib/firebase';
import { HR_COLLECTIONS, type HrLeaveRequest as HrLeaveRequestT } from '@/lib/hr';
import {
  PAYROLL_COLLECTIONS,
  unpaidLeaveDeduction,
  MONTH_LABELS,
  type HrDeduction as HrDeductionT,
  type HrLeaveAllocation as HrLeaveAllocationT,
} from '@/lib/hrPayroll';
import { unpaidDaysForLeave } from '@/lib/hrLeave';
import type { EncashmentLike, LeaveCalcOptions } from '@/lib/hrLeave';

const now = () => new Date().toISOString();

/** Mint a Firestore-style id without committing yet. */
export function newId(collectionName: string): string {
  return doc(collection(db, collectionName)).id;
}

/** Create or overwrite a doc with id, stamping timestamps. */
export async function hrSet<T extends Record<string, unknown>>(
  collectionName: string,
  id: string,
  data: T,
): Promise<void> {
  await setDoc(doc(db, collectionName, id), {
    ...data,
    createdAt: (data as { createdAt?: string }).createdAt ?? now(),
    updatedAt: now(),
  });
}

/** Add a new doc with auto-id, stamping createdAt/updatedAt. */
export async function hrAdd<T extends Record<string, unknown>>(
  collectionName: string,
  data: T,
): Promise<string> {
  const ref = await addDoc(collection(db, collectionName), {
    ...data,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
}

/** Patch fields on an existing doc, bumping updatedAt. */
export async function hrUpdate<T extends Record<string, unknown>>(
  collectionName: string,
  id: string,
  patch: T,
): Promise<void> {
  await updateDoc(doc(db, collectionName, id), {
    ...patch,
    updatedAt: now(),
  });
}

/** Delete a doc. */
export async function hrDelete(
  collectionName: string,
  id: string,
): Promise<void> {
  await deleteDoc(doc(db, collectionName, id));
}

/* ==================================================================
   Convenience wrappers per collection — keep callers terse.
   ================================================================== */
export const hr = {
  staff: {
    set: (id: string, data: Record<string, unknown>) =>
      hrSet(HR_COLLECTIONS.staff, id, data),
    update: (id: string, patch: Record<string, unknown>) =>
      hrUpdate(HR_COLLECTIONS.staff, id, patch),
    delete: (id: string) => hrDelete(HR_COLLECTIONS.staff, id),
  },
  complianceDocs: {
    add: (data: Record<string, unknown>) =>
      hrAdd(HR_COLLECTIONS.complianceDocs, data),
    update: (id: string, patch: Record<string, unknown>) =>
      hrUpdate(HR_COLLECTIONS.complianceDocs, id, patch),
    delete: (id: string) => hrDelete(HR_COLLECTIONS.complianceDocs, id),
  },
  leave: {
    add: (data: Record<string, unknown>) =>
      hrAdd(HR_COLLECTIONS.leave, data),
    update: (id: string, patch: Record<string, unknown>) =>
      hrUpdate(HR_COLLECTIONS.leave, id, patch),
  },
  contracts: {
    add: (data: Record<string, unknown>) =>
      hrAdd(HR_COLLECTIONS.contracts, data),
    update: (id: string, patch: Record<string, unknown>) =>
      hrUpdate(HR_COLLECTIONS.contracts, id, patch),
  },
  appraisals: {
    update: (id: string, patch: Record<string, unknown>) =>
      hrUpdate(HR_COLLECTIONS.appraisals, id, patch),
    add: (data: Record<string, unknown>) =>
      hrAdd(HR_COLLECTIONS.appraisals, data),
  },
  payrollRefs: {
    set: (staffId: string, data: Record<string, unknown>) =>
      hrSet(HR_COLLECTIONS.payrollRefs, staffId, data),
    update: (staffId: string, patch: Record<string, unknown>) =>
      hrUpdate(HR_COLLECTIONS.payrollRefs, staffId, patch),
  },

  /* ---- Payroll engine collections (Phase 1) ---------------------- */
  payrollProfile: {
    set: (staffId: string, data: Record<string, unknown>) =>
      hrSet(PAYROLL_COLLECTIONS.payrollProfile, staffId, data),
    update: (staffId: string, patch: Record<string, unknown>) =>
      hrUpdate(PAYROLL_COLLECTIONS.payrollProfile, staffId, patch),
  },
  deductions: {
    add: (data: Record<string, unknown>) =>
      hrAdd(PAYROLL_COLLECTIONS.deductions, data),
    update: (id: string, patch: Record<string, unknown>) =>
      hrUpdate(PAYROLL_COLLECTIONS.deductions, id, patch),
    delete: (id: string) => hrDelete(PAYROLL_COLLECTIONS.deductions, id),
  },
  allowanceCatalog: {
    add: (data: Record<string, unknown>) =>
      hrAdd(PAYROLL_COLLECTIONS.allowanceCatalog, data),
    update: (id: string, patch: Record<string, unknown>) =>
      hrUpdate(PAYROLL_COLLECTIONS.allowanceCatalog, id, patch),
    delete: (id: string) => hrDelete(PAYROLL_COLLECTIONS.allowanceCatalog, id),
  },
  settings: {
    set: (id: string, data: Record<string, unknown>) =>
      hrSet(PAYROLL_COLLECTIONS.settings, id, data),
    update: (id: string, patch: Record<string, unknown>) =>
      hrUpdate(PAYROLL_COLLECTIONS.settings, id, patch),
  },
  taxTables: {
    set: (id: string, data: Record<string, unknown>) =>
      hrSet(PAYROLL_COLLECTIONS.taxTables, id, data),
    update: (id: string, patch: Record<string, unknown>) =>
      hrUpdate(PAYROLL_COLLECTIONS.taxTables, id, patch),
  },
  leaveAllocations: {
    set: (staffId: string, data: Record<string, unknown>) =>
      hrSet(PAYROLL_COLLECTIONS.leaveAllocations, staffId, data),
    update: (staffId: string, patch: Record<string, unknown>) =>
      hrUpdate(PAYROLL_COLLECTIONS.leaveAllocations, staffId, patch),
  },
  payslips: {
    set: (id: string, data: Record<string, unknown>) =>
      hrSet(PAYROLL_COLLECTIONS.payslips, id, data),
    update: (id: string, patch: Record<string, unknown>) =>
      hrUpdate(PAYROLL_COLLECTIONS.payslips, id, patch),
  },
  gratuityPayout: {
    set: (id: string, data: Record<string, unknown>) =>
      hrSet(PAYROLL_COLLECTIONS.gratuityPayouts, id, data),
    delete: (id: string) => hrDelete(PAYROLL_COLLECTIONS.gratuityPayouts, id),
  },
  auditLog: {
    add: (data: Record<string, unknown>) =>
      hrAdd(PAYROLL_COLLECTIONS.auditLog, data),
  },
};

export { serverTimestamp };

/* ------------------------------------------------------------------
   Keep the unpaid-leave charge in step with its leave request.
   Called after a leave is approved, edited (extended / cut short) or
   cancelled, so the "Unpaid leave — N days" deduction always matches
   the leave's current days and status.
   ------------------------------------------------------------------ */
export async function syncUnpaidLeaveDeduction(args: {
  leave: HrLeaveRequestT;
  allocation: HrLeaveAllocationT | undefined;
  staffLeave: HrLeaveRequestT[];
  basicSalary: number;
  deductions: HrDeductionT[];
  /** The person's leave sales. Without them, leave taken out of days that
   *  were sold is priced as paid - the same day paid for twice. */
  staffEncashments?: EncashmentLike[];
  /** Accrual anchor / start date / leave year, from lib/leavePolicy leaveBasis. */
  calc?: LeaveCalcOptions;
}): Promise<void> {
  const { leave, allocation, staffLeave, basicSalary, deductions, staffEncashments, calc } = args;
  const start = new Date(leave.startDate);
  const period = { year: start.getFullYear(), month: start.getMonth() + 1 };

  // Existing charge for this leave — by explicit link, or the legacy
  // heuristic for charges created before the link field existed.
  const existing = deductions.find((d) =>
    d.staffId === leave.staffId && (
      d.sourceLeaveId === leave.id ||
      (!d.sourceLeaveId && d.type === 'other' &&
        typeof d.label === 'string' && d.label.startsWith('Unpaid leave —') &&
        d.oneOffPeriod?.year === period.year && d.oneOffPeriod?.month === period.month)
    ),
  );

  const unpaidDays = unpaidDaysForLeave(leave, allocation, staffLeave, staffEncashments, calc);

  // Now fully within balance / cancelled / rejected → no charge.
  if (unpaidDays <= 0) {
    if (existing) await hr.deductions.delete(existing.id);
    return;
  }
  // Can't price it without a basic salary — leave any existing charge as-is.
  if (!(basicSalary > 0)) return;

  const amount = unpaidLeaveDeduction(basicSalary, unpaidDays);
  const label = `Unpaid leave — ${unpaidDays} day${unpaidDays === 1 ? '' : 's'} (${MONTH_LABELS[period.month - 1]} ${period.year})`;
  const customFields = [{ key: 'Basis', value: `${unpaidDays} unpaid day(s) × day's basic pay` }];
  const payload = {
    staffId: leave.staffId, type: 'other', label, status: 'active',
    values: { amount }, customFields, oneOffPeriod: period, sourceLeaveId: leave.id,
  };

  if (existing) await hr.deductions.update(existing.id, payload);
  else await hr.deductions.add(payload);
}
